import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Cookie, X, Check, ShieldCheck, BarChart3 } from 'lucide-react';
import { i18nInstance } from '../../../lib/i18n.js';
import { enableSentryPerformance, disableSentryPerformance } from '../../../lib/sentry.js';
import { COOKIE_PREFERENCES_EVENT } from '../../../lib/cookie-consent.js';
import { detectPrivacySignal } from '../../../lib/privacy.js';
import { cn } from '../../../lib/cn.js';

const COOKIE_NAME = 'cc_cookie';
const COOKIE_EXPIRY_DAYS = 365;

// Bump when cookie purposes/services materially change, so prior consent is
// re-solicited (GDPR specificity + CNIL ~13-month renewal). Stored in the cookie
// and compared on load; a lower stored revision re-shows the banner.
const CONSENT_REVISION = 1;

type CategoryId = 'necessary' | 'analytics' | 'marketing';

interface StoredConsent {
  categories: CategoryId[];
  revision: number;
  ts: number;
}

const CATEGORY_ORDER: CategoryId[] = ['necessary', 'analytics', 'marketing'];

function readStoredConsent(): StoredConsent | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)cc_cookie=([^;]*)/);
  if (!match?.[1]) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(match[1])) as Partial<StoredConsent>;
    if (!Array.isArray(parsed.categories)) return null;
    return {
      categories: parsed.categories.filter((c): c is CategoryId =>
        CATEGORY_ORDER.includes(c as CategoryId),
      ),
      revision: parsed.revision ?? 0,
      ts: parsed.ts ?? Date.now(),
    };
  } catch {
    return null;
  }
}

function writeConsent(categories: CategoryId[]) {
  if (typeof document === 'undefined') return;
  const value: StoredConsent = { categories, revision: CONSENT_REVISION, ts: Date.now() };
  const expires = new Date(Date.now() + COOKIE_EXPIRY_DAYS * 86400 * 1000).toUTCString();
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(
    JSON.stringify(value),
  )}; path=/; expires=${expires}; SameSite=Lax`;
}

function eraseAnalyticsCookies() {
  if (typeof document === 'undefined') return;
  const names = ['_ga', '_gid', '_gat'];
  for (const cookie of document.cookie.split(';')) {
    const [rawName] = cookie.split('=');
    const name = rawName?.trim();
    if (!name) continue;
    if (names.includes(name) || name.startsWith('_ga')) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    }
  }
}

function applyConsent(categories: CategoryId[]) {
  if (categories.includes('analytics')) {
    enableSentryPerformance();
  } else {
    disableSentryPerformance();
    eraseAnalyticsCookies();
  }
}

interface CategoryMeta {
  id: CategoryId;
  icon: typeof Cookie;
  tint: string;
  readOnly?: boolean;
}

// "marketing" is intentionally omitted: no marketing/advertising service is
// wired to it, so a toggle that does nothing would mislead the user. Re-add it
// (and bump CONSENT_REVISION) when a real marketing service is introduced.
const CATEGORIES: CategoryMeta[] = [
  { id: 'necessary', icon: ShieldCheck, tint: 'text-accent-lime', readOnly: true },
  { id: 'analytics', icon: BarChart3, tint: 'text-accent-cyan' },
];

export function CookieBanner() {
  const { t, i18n } = useTranslation('consent');
  const [ready, setReady] = useState(false);
  const [visible, setVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState<Record<CategoryId, boolean>>({
    necessary: true,
    analytics: false,
    marketing: false,
  });
  const firstFocusRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let cancelled = false;

    const onLang = () => {
      // re-render through i18n subscription; no state mutation needed
    };

    (async () => {
      await i18nInstance.loadNamespaces('consent');
      if (cancelled) return;

      const stored = readStoredConsent();
      const privacySignal = detectPrivacySignal();

      if (stored && stored.revision >= CONSENT_REVISION) {
        applyConsent(stored.categories);
        setSelected({
          necessary: true,
          analytics: stored.categories.includes('analytics'),
          marketing: stored.categories.includes('marketing'),
        });
        setVisible(false);
      } else if (privacySignal) {
        // GPC or DNT signal: silently store necessary-only, no banner
        writeConsent(['necessary']);
        applyConsent(['necessary']);
        setVisible(false);
      } else {
        // No consent yet, or a stale revision after a policy change: re-ask.
        setVisible(true);
      }
      setReady(true);
    })().catch(console.error);

    i18nInstance.on('languageChanged', onLang);

    const onOpenPreferences = () => setModalOpen(true);
    window.addEventListener(COOKIE_PREFERENCES_EVENT, onOpenPreferences);

    return () => {
      cancelled = true;
      i18nInstance.off('languageChanged', onLang);
      window.removeEventListener(COOKIE_PREFERENCES_EVENT, onOpenPreferences);
    };
  }, []);

  // Focus management when modal opens
  useEffect(() => {
    if (modalOpen) {
      firstFocusRef.current?.focus();
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') setModalOpen(false);
      };
      window.addEventListener('keydown', onKey);
      return () => window.removeEventListener('keydown', onKey);
    }
    return undefined;
  }, [modalOpen]);

  const save = useCallback((chosen: CategoryId[]) => {
    const final = Array.from(new Set<CategoryId>(['necessary', ...chosen]));
    writeConsent(final);
    applyConsent(final);
    setVisible(false);
    setModalOpen(false);
  }, []);

  const handleAcceptAll = () => save(['necessary', 'analytics']);
  const handleRejectAll = () => save(['necessary']);
  const handleSaveCustom = () => {
    const chosen = CATEGORY_ORDER.filter((c) => selected[c]);
    save(chosen);
  };

  const toggle = (id: CategoryId, on: boolean) => {
    if (id === 'necessary') return;
    setSelected((prev) => ({ ...prev, [id]: on }));
  };

  // Nothing renders until i18n is ready + we know whether to show
  if (!ready || (!visible && !modalOpen)) return null;

  // Key used in i18n changes ignored by effect — use language to force a remount when it swaps
  const lang = i18n.language;

  return (
    <div aria-live="polite" data-lang={lang}>
      {visible && !modalOpen && (
        <CookieSlab
          onAcceptAll={handleAcceptAll}
          onRejectAll={handleRejectAll}
          onCustomize={() => setModalOpen(true)}
          t={t}
        />
      )}
      {modalOpen && (
        <CookieModal
          firstFocusRef={firstFocusRef}
          onClose={() => setModalOpen(false)}
          onAcceptAll={handleAcceptAll}
          onSave={handleSaveCustom}
          selected={selected}
          onToggle={toggle}
          t={t}
        />
      )}
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TFn = (key: string, options?: any) => string;

interface SlabProps {
  onAcceptAll: () => void;
  onRejectAll: () => void;
  onCustomize: () => void;
  t: TFn;
}

function CookieSlab({ onAcceptAll, onRejectAll, onCustomize, t }: SlabProps) {
  return (
    <div
      role="dialog"
      aria-label={t('banner.title')}
      className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4 sm:left-auto sm:right-4 sm:w-[min(420px,calc(100vw-2rem))]"
    >
      <div
        className={cn(
          'agb-cookie-slab relative overflow-hidden rounded-xl border border-white/10 bg-surface/90 p-5 backdrop-blur-md',
          'shadow-[0_30px_80px_-20px_rgba(37,110,255,0.35)]',
        )}
      >
        {/* Gradient edge glow */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -inset-px rounded-xl"
          style={{
            background:
              'linear-gradient(140deg, rgba(221,252,116,0.4), rgba(45,225,252,0.2) 40%, transparent 70%)',
            padding: 1,
            WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
          }}
        />

        <div className="relative flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-white/10 bg-white/[0.03]">
            <Cookie className="h-4 w-4 text-accent-lime" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-small font-semibold text-primary">{t('banner.title')}</h2>
            <p className="mt-1.5 text-caption leading-relaxed text-secondary">
              {t('banner.description')}
            </p>
          </div>
        </div>

        <div className="relative mt-4 flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={onAcceptAll}
            className="inline-flex items-center justify-center gap-1.5 rounded-md bg-accent-lime px-4 py-2 text-small font-semibold text-canvas transition-[filter,transform] duration-150 hover:brightness-105 active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400 sm:flex-1"
          >
            {t('banner.acceptAll')}
          </button>
          <button
            type="button"
            onClick={onRejectAll}
            className="inline-flex items-center justify-center rounded-md border border-white/10 bg-white/[0.03] px-4 py-2 text-small font-medium text-primary transition-colors hover:border-white/20 hover:bg-white/[0.06] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400 sm:flex-1"
          >
            {t('banner.rejectAll')}
          </button>
        </div>
        <button
          type="button"
          onClick={onCustomize}
          className="link-underline-draw relative mt-3 inline-flex items-center gap-1.5 text-caption text-accent-blue focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
        >
          {t('banner.managePreferences')}
        </button>
      </div>
    </div>
  );
}

interface ModalProps {
  firstFocusRef: React.RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  onAcceptAll: () => void;
  onSave: () => void;
  selected: Record<CategoryId, boolean>;
  onToggle: (id: CategoryId, on: boolean) => void;
  t: TFn;
}

function CookieModal({
  firstFocusRef,
  onClose,
  onAcceptAll,
  onSave,
  selected,
  onToggle,
  t,
}: ModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label={t('preferences.close')}
        onClick={onClose}
        className="absolute inset-0 bg-canvas/70 backdrop-blur-sm"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-pref-title"
        className="relative w-full max-w-lg overflow-hidden rounded-xl border border-white/10 bg-surface shadow-[0_60px_120px_-30px_rgba(0,0,0,0.8)]"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -inset-px rounded-xl"
          style={{
            background:
              'linear-gradient(140deg, rgba(221,252,116,0.35), rgba(45,225,252,0.25) 40%, transparent 70%)',
            padding: 1,
            WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
          }}
        />

        <header className="relative flex items-start justify-between gap-3 border-b border-white/[0.04] p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-white/10 bg-white/[0.03]">
              <Cookie className="h-4 w-4 text-accent-lime" aria-hidden="true" />
            </div>
            <div>
              <h2 id="cookie-pref-title" className="text-h3 font-semibold text-primary">
                {t('preferences.title')}
              </h2>
              <p className="mt-1 text-small text-secondary">{t('banner.description')}</p>
            </div>
          </div>
          <button
            ref={firstFocusRef}
            type="button"
            onClick={onClose}
            aria-label={t('preferences.close')}
            className="rounded-md p-1.5 text-secondary transition-colors hover:bg-white/[0.06] hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </header>

        <div className="relative max-h-[60vh] space-y-3 overflow-y-auto p-5">
          {CATEGORIES.map(({ id, icon: Icon, tint, readOnly }) => {
            const on = selected[id];
            return (
              <div
                key={id}
                className={cn(
                  'rounded-lg border p-4 transition-colors',
                  on
                    ? 'border-white/10 bg-white/[0.03]'
                    : 'border-white/[0.04] bg-white/[0.01] hover:bg-white/[0.02]',
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-white/[0.06] bg-white/[0.03]">
                      <Icon className={cn('h-4 w-4', tint)} aria-hidden="true" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-small font-semibold text-primary">
                          {t(`categories.${id}.title`)}
                        </h3>
                        {readOnly && (
                          <span className="rounded-sm border border-accent-lime/30 bg-accent-lime/10 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-accent-lime">
                            Required
                          </span>
                        )}
                      </div>
                      <p className="mt-1.5 text-caption leading-relaxed text-secondary">
                        {t(`categories.${id}.description`)}
                      </p>
                    </div>
                  </div>
                  <ToggleSwitch
                    on={on}
                    disabled={readOnly}
                    ariaLabel={t(`categories.${id}.title`)}
                    onChange={(v) => onToggle(id, v)}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <footer className="relative flex flex-col gap-2 border-t border-white/[0.04] bg-canvas/40 p-5 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onSave}
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-white/10 bg-white/[0.03] px-4 py-2 text-small font-medium text-primary transition-colors hover:border-white/20 hover:bg-white/[0.06] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
          >
            <Check className="h-4 w-4" aria-hidden="true" />
            {t('preferences.save')}
          </button>
          <button
            type="button"
            onClick={onAcceptAll}
            className="inline-flex items-center justify-center rounded-md bg-accent-lime px-4 py-2 text-small font-semibold text-canvas transition-[filter,transform] duration-150 hover:brightness-105 active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
          >
            {t('preferences.acceptAll')}
          </button>
        </footer>
      </div>
    </div>
  );
}

interface ToggleSwitchProps {
  on: boolean;
  disabled?: boolean | undefined;
  ariaLabel: string;
  onChange: (v: boolean) => void;
}

function ToggleSwitch({ on, disabled = false, ariaLabel, onChange }: ToggleSwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onChange(!on)}
      style={{ width: 44, height: 24 }}
      className={cn(
        'relative inline-flex shrink-0 items-center rounded-full border transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400',
        on ? 'border-accent-lime/60 bg-accent-lime' : 'border-white/15 bg-white/[0.12]',
        disabled && 'cursor-not-allowed opacity-60',
      )}
    >
      {/* Geometry is inline px because this project overrides Tailwind's spacing
          scale with non-linear tokens, which breaks numeric w-/h-/translate-x
          utilities. Colors stay in Tailwind. 44x24 track, 18px knob, slide 3->21. */}
      <span
        aria-hidden="true"
        className="inline-block rounded-full bg-white shadow-sm transition-transform duration-200"
        style={{ width: 18, height: 18, transform: on ? 'translateX(21px)' : 'translateX(3px)' }}
      />
    </button>
  );
}
